// Basket planner — the household needs model, fully editable. People + tier +
// cadence seed a suggested staple basket (grounded in FEWS NET / WFP figures);
// users then edit quantities, REMOVE rows they don't need, and ADD rows looked
// up from the live inventory. Tier scales the suggested basket uniformly;
// per-item edits handle preferences, with a flag when a suggested item drifts
// more than ±50%. Tooltips explain the derivation. The pro-forma PDF / share /
// email all reflect the final edited basket. Shared by customer + recipient.

import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Users, Calculator, Copy, ArrowRight, Info, AlertTriangle, RotateCcw, Printer, Share2, Mail, Send, Plus, X, Search } from 'lucide-react';
import { toast } from 'sonner';
import { PortalShell } from '../../components/shared/PortalShell';
import { Card, SectionHeader } from '../../components/shared/primitives';
import { Button } from '../../components/stripe-ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import { CUSTOMER_NAV, RECIPIENT_NAV } from '../_navs';
import { needs, catalog, type NeedsTier, type NeedsCadence, type NeedsEstimate, type InventoryItem } from '../../lib/api';
import { formatHtgMinor } from '../../lib/money';

const TIERS: { v: NeedsTier; label: string; hint: string }[] = [
  { v: 'low', label: 'Low', hint: 'tighter budget' },
  { v: 'medium', label: 'Medium', hint: 'typical' },
  { v: 'high', label: 'High', hint: 'generous' },
];
const CADENCES: { v: NeedsCadence; label: string }[] = [
  { v: 'weekly', label: 'Weekly' },
  { v: 'biweekly', label: 'Every 2 weeks' },
  { v: 'monthly', label: 'Monthly' },
];
const FLAG_LOW = 0.5;
const FLAG_HIGH = 1.5;

interface Row {
  key: string;
  group: string;
  label: string;
  unit: string;
  quantity: number;
  est_unit_htg: number;
  isCustom: boolean;
  suggested?: number;        // config baseline qty (for the ±50% flag)
  per_person_week?: number;
  priceSource?: 'market' | 'reference';
  priceN?: number;
  priceLow?: number | null;
  priceHigh?: number | null;
}

export function BasketPlannerPage() {
  const { user } = useAuth();
  const isRecipient = user?.role === 'recipient';
  const nav = isRecipient ? RECIPIENT_NAV : CUSTOMER_NAV;
  const newRequestHref = isRecipient ? '/recipient/request/new' : '/customer/requests/new';

  const [people, setPeople] = useState(4);
  const [tier, setTier] = useState<NeedsTier>('medium');
  const [cadence, setCadence] = useState<NeedsCadence>('monthly');
  const [overrides, setOverrides] = useState<Record<string, number>>({});  // config qty edits
  const [removed, setRemoved] = useState<Set<string>>(new Set());           // hidden config keys
  const [customRows, setCustomRows] = useState<Row[]>([]);                  // added-from-inventory
  useEffect(() => { setOverrides({}); }, [people, tier, cadence]);           // suggested qty changes

  const q = useQuery({ queryKey: ['needs', people, tier, cadence], queryFn: () => needs.estimate(people, tier, cadence), enabled: people >= 1 });
  const est = q.data;

  const rows: Row[] = useMemo(() => {
    if (!est) return [];
    const config = est.items
      .filter((i) => !removed.has(i.key))
      .map((i): Row => ({ key: i.key, group: i.group, label: i.label, unit: i.unit, quantity: overrides[i.key] ?? i.quantity, est_unit_htg: i.est_unit_htg, isCustom: false, suggested: i.quantity, per_person_week: i.per_person_week, priceSource: i.price_source, priceN: i.price_n, priceLow: i.price_low, priceHigh: i.price_high }));
    return [...config, ...customRows.map((c) => ({ ...c, priceSource: 'market' as const }))];
  }, [est, overrides, removed, customRows]);

  const totalHtgMinor = rows.reduce((a, r) => a + Math.round(r.quantity * r.est_unit_htg * 100), 0);
  const totalUsd = est ? totalHtgMinor / 100 / est.fx_htg_per_usd : 0;
  const groups = est ? [...est.groups, { key: 'custom', label: 'Added items' }] : [];
  const dirty = Object.keys(overrides).length > 0 || removed.size > 0 || customRows.length > 0;

  const lineItems = () => rows.map((r) => ({ label: r.label, unit: r.unit, quantity: r.quantity, est_unit_htg: r.est_unit_htg, group: r.group }));
  const payload = () => ({ people, tier, cadence, items: lineItems() });

  const setQty = (row: Row, v: string) => {
    const num = v === '' ? 0 : Math.max(0, Number(v));
    if (row.isCustom) {
      setCustomRows((cs) => cs.map((c) => (c.key === row.key ? { ...c, quantity: num } : c)));
    } else {
      setOverrides((o) => {
        const next = { ...o };
        if (v !== '' && num === row.suggested) delete next[row.key];
        else next[row.key] = num;
        return next;
      });
    }
  };
  const removeRow = (row: Row) => {
    if (row.isCustom) setCustomRows((cs) => cs.filter((c) => c.key !== row.key));
    else setRemoved((s) => new Set(s).add(row.key));
  };
  const resetAll = () => { setOverrides({}); setRemoved(new Set()); setCustomRows([]); };

  // Add-from-inventory search
  const [searchQ, setSearchQ] = useState('');
  const search = useQuery({ queryKey: ['inv-search', searchQ], queryFn: () => catalog.items(searchQ), enabled: searchQ.trim().length >= 2 });
  const addItem = (it: InventoryItem) => {
    const key = `custom:${it.name}:${it.unit}`;
    if (rows.some((r) => r.key === key)) { toast.info('Already in the basket'); return; }
    setCustomRows((cs) => [...cs, { key, group: 'custom', label: it.name, unit: it.unit, quantity: 1, est_unit_htg: it.est_unit_htg, isCustom: true }]);
    setSearchQ('');
    toast.success(`Added ${it.name}`);
  };

  const copyList = () => {
    const header = `Basket — ${people} ${people === 1 ? 'person' : 'people'}, ${tier} tier, ${cadence}:\n`;
    navigator.clipboard.writeText(header + rows.map((r) => `${r.label}: ${r.quantity} ${r.unit}`).join('\n')).then(
      () => toast.success('Shopping list copied'),
      () => toast.error('Could not copy'),
    );
  };

  const [busy, setBusy] = useState<null | 'pdf' | 'share' | 'email'>(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailAddr, setEmailAddr] = useState('');

  const openPdf = async () => {
    setBusy('pdf');
    try {
      const blob = await needs.basketPdf(payload());
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) { toast.error((e as Error).message || 'Could not open the PDF'); }
    finally { setBusy(null); }
  };
  const sharePdf = async () => {
    setBusy('share');
    try {
      const blob = await needs.basketPdf(payload());
      const file = new File([blob], 'lakaycart-basket-plan.pdf', { type: 'application/pdf' });
      const n = navigator as Navigator & { canShare?: (d?: unknown) => boolean };
      if (n.canShare?.({ files: [file] })) {
        await n.share({ files: [file], title: 'VoyeLakay basket plan', text: `Household basket plan — ${people} people, ${tier} tier, ${cadence}.` } as ShareData);
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = 'lakaycart-basket-plan.pdf'; a.click();
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
        toast.success('Plan downloaded — share the file');
      }
    } catch (e) { if ((e as Error).name !== 'AbortError') toast.error((e as Error).message || 'Could not share'); }
    finally { setBusy(null); }
  };
  const sendEmail = async () => {
    if (!emailAddr.trim()) return;
    setBusy('email');
    try {
      await needs.emailBasket({ ...payload(), email: emailAddr.trim() });
      toast.success(`Plan sent to ${emailAddr.trim()}`);
      setEmailOpen(false); setEmailAddr('');
    } catch (e) { toast.error((e as Error).message || 'Could not send'); }
    finally { setBusy(null); }
  };

  const renderRow = (r: Row, e: NeedsEstimate) => {
    const ratio = r.suggested && r.suggested > 0 ? r.quantity / r.suggested : 1;
    const flagged = !r.isCustom && r.quantity > 0 && (ratio < FLAG_LOW || ratio > FLAG_HIGH);
    const edited = !r.isCustom && overrides[r.key] !== undefined;
    const lineHtgMinor = Math.round(r.quantity * r.est_unit_htg * 100);
    return (
      <tr key={r.key} className="hover:bg-slate-50 group/row">
        <td className="py-2.5 px-5">
          <span className="inline-flex items-center gap-1.5 font-medium text-slate-800">
            {r.label}
            {r.isCustom
              ? <span className="text-[9px] uppercase tracking-wide font-semibold text-brand-600 bg-brand-50 px-1 rounded">added</span>
              : (
                <InfoTip>
                  <b>{r.label}</b><br />
                  Baseline: {r.per_person_week} {r.unit}/person/week.<br />
                  Suggested = {r.per_person_week} × {people} {people === 1 ? 'person' : 'people'} × {e.tier_factor} ({e.tier}) × {e.weeks} wk ≈ <b>{r.suggested} {r.unit}</b>.
                </InfoTip>
              )}
          </span>
        </td>
        <td className="py-2 px-3">
          <div className="flex items-center justify-center gap-1.5">
            <input
              type="number" min={0} step="0.1" value={r.quantity}
              onChange={(ev) => setQty(r, ev.target.value)}
              className={`w-16 px-2 py-1 text-[13px] text-right font-mono tabular-nums border rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500/30 ${flagged ? 'border-amber-400 bg-amber-50' : edited ? 'border-brand-300' : 'border-slate-200'}`}
            />
            <span className="text-slate-400 text-[12px] w-8">{r.unit}</span>
            {flagged
              ? <InfoTip icon={<AlertTriangle className="w-3.5 h-3.5 text-amber-500" />}>
                  This is {Math.round(ratio * 100)}% of the typical amount ({r.suggested} {r.unit}) for this household — more than ±50% off. Double-check it's intended.
                </InfoTip>
              : <span className="w-3.5" />}
          </div>
        </td>
        <td className="py-2.5 px-3 text-right">
          <span className="inline-flex items-center gap-1 justify-end">
            <span className="font-mono text-slate-500">{formatHtgMinor(lineHtgMinor, { withSymbol: false })}</span>
            <InfoTip icon={<span className={`text-[8.5px] font-bold uppercase px-1 py-px rounded ${r.priceSource === 'market' ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-400'}`}>{r.priceSource === 'market' ? 'avg' : 'est'}</span>}>
              {r.priceSource === 'market'
                ? <>≈ {r.est_unit_htg} HTG/{r.unit} — <b>market average</b>{r.priceN ? ` from ${r.priceN} merchant${r.priceN > 1 ? 's' : ''}` : ''}{(r.priceLow != null && r.priceHigh != null && r.priceLow !== r.priceHigh) ? ` (range ${r.priceLow}–${r.priceHigh})` : ''}. Your merchant's quote is the final price.</>
                : <>≈ {r.est_unit_htg} HTG/{r.unit} — <b>reference estimate</b> (no inventory match yet). Your merchant's quote is the final price.</>}
            </InfoTip>
          </span>
        </td>
        <td className="py-2.5 px-4 text-right">
          <button onClick={() => removeRow(r)} title="Remove" className="p-1 rounded text-slate-300 hover:text-rose-600 hover:bg-rose-50 opacity-0 group-hover/row:opacity-100 transition-opacity">
            <X className="w-3.5 h-3.5" />
          </button>
        </td>
      </tr>
    );
  };

  return (
    <PortalShell nav={nav} activeId="planner" title="Basket planner" subtitle="Estimate a household's needs, then edit, remove, or add items.">
      <div className="grid lg:grid-cols-5 gap-5">
        {/* Inputs */}
        <Card className="lg:col-span-2 space-y-5 self-start">
          <SectionHeader title="Household" subtitle="Who are we feeding, and how often?" />
          <div>
            <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">People to feed</label>
            <div className="mt-1.5 flex items-center gap-3">
              <button onClick={() => setPeople((p) => Math.max(1, p - 1))} className="w-9 h-9 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 text-lg leading-none">−</button>
              <div className="flex items-center gap-1.5 min-w-[3rem] justify-center"><Users className="w-4 h-4 text-slate-400" /><span className="text-[22px] font-bold tabular-nums text-slate-900">{people}</span></div>
              <button onClick={() => setPeople((p) => Math.min(50, p + 1))} className="w-9 h-9 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 text-lg leading-none">+</button>
            </div>
          </div>
          <div>
            <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-semibold text-slate-500">
              Consumption tier
              <InfoTip>Tier scales the <b>whole basket</b> uniformly: Low ×0.7, Medium ×1.0, High ×1.4 of the per-person average. For individual items (more rice, no flour), edit or remove rows in the basket.</InfoTip>
            </span>
            <div className="mt-1.5 grid grid-cols-3 gap-1.5">
              {TIERS.map((t) => (
                <button key={t.v} onClick={() => setTier(t.v)} className={`px-2 py-2 rounded-lg border text-center transition-colors ${tier === t.v ? 'border-brand-500 bg-brand-50 text-brand-800' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                  <span className="block text-[13px] font-semibold">{t.label}</span><span className="block text-[10px] text-slate-400">{t.hint}</span>
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">Cadence</label>
            <div className="mt-1.5 grid grid-cols-3 gap-1.5">
              {CADENCES.map((c) => (
                <button key={c.v} onClick={() => setCadence(c.v)} className={`px-2 py-2 rounded-lg border text-[12.5px] font-semibold transition-colors ${cadence === c.v ? 'border-brand-500 bg-brand-50 text-brand-800' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>{c.label}</button>
              ))}
            </div>
          </div>
          {dirty && (
            <button onClick={resetAll} className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-slate-500 hover:text-slate-900">
              <RotateCcw className="w-3.5 h-3.5" /> Reset to suggested basket
            </button>
          )}
        </Card>

        {/* Basket */}
        <Card className="lg:col-span-3 p-0" padding="p-0">
          <div className="p-5 border-b border-slate-100 flex items-center justify-between gap-2">
            <SectionHeader title="Basket" subtitle={est ? `${rows.length} items · ${cadence} · edit, remove, or add` : undefined} />
            {q.isFetching && <Loader2 className="w-4 h-4 animate-spin text-slate-300" />}
          </div>

          {/* Add from inventory */}
          <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/50">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <input
                value={searchQ} onChange={(e) => setSearchQ(e.target.value)}
                placeholder="Add an item from inventory — search e.g. “rice”, “diapers”…"
                className="w-full pl-8 pr-3 py-2 text-[13px] border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500/30"
              />
              {searchQ.trim().length >= 2 && (
                <div className="absolute z-10 mt-1 w-full max-h-60 overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg">
                  {search.isLoading ? (
                    <div className="px-3 py-2 text-[12px] text-slate-400"><Loader2 className="inline w-3.5 h-3.5 animate-spin" /> Searching…</div>
                  ) : (search.data ?? []).length === 0 ? (
                    <div className="px-3 py-2 text-[12px] text-slate-400">No inventory match.</div>
                  ) : (
                    (search.data ?? []).map((it) => (
                      <button key={`${it.name}:${it.unit}`} onClick={() => addItem(it)} className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-[13px] hover:bg-brand-50">
                        <span className="text-slate-800">{it.name} <span className="text-slate-400 text-[11px]">/ {it.unit}</span></span>
                        <span className="inline-flex items-center gap-2 shrink-0"><span className="font-mono text-[12px] text-slate-500">~{it.est_unit_htg} HTG</span><Plus className="w-3.5 h-3.5 text-brand-600" /></span>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>

          {q.isLoading ? (
            <div className="text-center py-16 text-slate-400 text-[13px]"><Loader2 className="inline w-4 h-4 animate-spin" /> Calculating…</div>
          ) : est ? (
            <>
              <table className="text-[13px] w-full">
                <thead className="text-[10.5px] uppercase tracking-wider text-slate-400 bg-slate-50">
                  <tr><th className="text-left py-2.5 px-5 font-medium">Item</th><th className="text-center py-2.5 px-3 font-medium">Quantity</th><th className="text-right py-2.5 px-3 font-medium">Est.</th><th className="w-10"></th></tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {groups.map((g) => {
                    const gr = rows.filter((r) => r.group === g.key);
                    if (!gr.length) return null;
                    return (
                      <Fragment key={g.key}>
                        <tr className="bg-slate-50/80"><td colSpan={4} className="py-1.5 px-5 text-[10px] uppercase tracking-wider font-semibold text-slate-500">{g.label}</td></tr>
                        {gr.map((r) => renderRow(r, est))}
                      </Fragment>
                    );
                  })}
                  {rows.length === 0 && <tr><td colSpan={4} className="py-8 text-center text-[13px] text-slate-400">Basket is empty — add items above or reset.</td></tr>}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-slate-200 bg-slate-50/60">
                    <td className="py-3 px-5 font-semibold text-slate-900">Estimated total{dirty && <span className="ml-2 text-[10px] font-sans font-semibold text-brand-600 uppercase tracking-wide">edited</span>}</td>
                    <td></td>
                    <td className="py-3 px-3 text-right" colSpan={2}>
                      <span className="block font-mono font-bold text-slate-900">{formatHtgMinor(totalHtgMinor)}</span>
                      <span className="block text-[11px] text-slate-400">≈ ${totalUsd.toFixed(2)} USD</span>
                    </td>
                  </tr>
                </tfoot>
              </table>

              <div className="p-4 border-t border-slate-100 space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Link to={newRequestHref}><Button size="sm" leadingIcon={<ArrowRight className="w-3.5 h-3.5" />}>Start a grocery request</Button></Link>
                  <span className="mx-1 h-5 w-px bg-slate-200" />
                  <Button tone="outline" size="sm" onClick={openPdf} loading={busy === 'pdf'} leadingIcon={<Printer className="w-3.5 h-3.5" />}>Pro forma PDF</Button>
                  <Button tone="outline" size="sm" onClick={sharePdf} loading={busy === 'share'} leadingIcon={<Share2 className="w-3.5 h-3.5" />}>Share</Button>
                  <Button tone="outline" size="sm" onClick={() => setEmailOpen((v) => !v)} leadingIcon={<Mail className="w-3.5 h-3.5" />}>Send</Button>
                  <Button tone="outline" size="sm" onClick={copyList} leadingIcon={<Copy className="w-3.5 h-3.5" />}>Copy list</Button>
                </div>
                {emailOpen && (
                  <div className="flex items-center gap-2">
                    <input type="email" value={emailAddr} onChange={(e) => setEmailAddr(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') sendEmail(); }} placeholder="name@email.com" className="w-56 px-3 py-1.5 text-[13px] border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500/30" />
                    <Button size="sm" onClick={sendEmail} loading={busy === 'email'} disabled={!emailAddr.trim()} leadingIcon={<Send className="w-3.5 h-3.5" />}>Send plan</Button>
                  </div>
                )}
              </div>

              <div className="px-5 pb-5">
                <p className="flex items-start gap-1.5 text-[11px] text-slate-400 leading-relaxed">
                  <Info className="w-3.5 h-3.5 shrink-0 mt-px" />
                  Prices are <b className="font-semibold text-emerald-600">market averages</b> across merchants (unit-normalized median), or a <b className="font-semibold text-slate-500">reference estimate</b> where inventory has no match — a planning figure at FX ≈ {est.fx_htg_per_usd.toFixed(2)} HTG/USD. The <b className="font-semibold">final price is your merchant's quote</b> at order time.
                </p>
              </div>
            </>
          ) : (
            <div className="text-center py-16 text-slate-400 text-[13px]"><Calculator className="inline w-4 h-4" /> Set a household above.</div>
          )}
        </Card>
      </div>
    </PortalShell>
  );
}

function InfoTip({ children, icon }: { children: React.ReactNode; icon?: React.ReactNode }) {
  return (
    <span className="relative inline-flex group align-middle">
      {icon ?? <Info className="w-3.5 h-3.5 text-slate-300 hover:text-slate-500 cursor-help" />}
      <span className="pointer-events-none absolute left-1/2 bottom-full z-30 mb-1.5 hidden -translate-x-1/2 group-hover:block w-60 rounded-lg bg-slate-900 px-3 py-2 text-[11px] font-normal normal-case tracking-normal leading-relaxed text-slate-100 shadow-xl">
        {children}
      </span>
    </span>
  );
}
